import {DashboardFeedbackDialog, type FeedbackDialogRef} from '@github-ui/dashboard-feedback-dialog'
import type {SidebarLink} from '@github-ui/saved-views'
import {DashboardSidebarCollapsed} from '@github-ui/shared-dashboard-sidebar/DashboardSidebarCollapsed'
/* eslint-disable-next-line primer-react/no-deprecated-flash -- Existing Flash usage should be migrated to Banner. */
import {ActionList, ActionMenu, Button, Flash, Label, Truncate} from '@primer/react'
import {ArrowSwitchIcon, ArrowUpRightIcon, CommentIcon, RocketIcon} from '@primer/octicons-react'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {clsx} from 'clsx'
import {useRef, type CSSProperties, type RefObject} from 'react'

import type {DefaultViewDashboardType} from '../../constants/default-view'
import {OptionsButton, type DeleteViewHandler} from './OptionsButton'

import styles from './SharedHeader.module.css'

const HEADER_ROW_WITH_FILTER_STYLE: CSSProperties = {marginBlockEnd: 'var(--base-size-16)'}

interface SharedHeaderProps {
  dashboardType: DefaultViewDashboardType
  links: SidebarLink[]
  selectedLink: SidebarLink | undefined
  onSelectLink: (linkItem: SidebarLink) => void
  savedViews: SidebarLink[]
  showFeedbackDialog?: boolean
  feedbackSubject?: string
  feedbackPlaceholderText?: string
  filter?: React.ReactElement
  rightSideContent?: React.ReactElement
  primaryAction?: React.ReactElement
  previewMenu?: React.ReactElement
  showSavedViews?: boolean
  onCreateView?: () => void
  onEditView?: (shortcut: SidebarLink) => void
  onDeleteView?: DeleteViewHandler
  onUpdateView?: (shortcut: SidebarLink) => void
  isViewModified?: boolean
  deleteErrorMessage?: string
  newButton?: NewButtonProps
  defaultViewId?: string
  generateLinkPath: (path: string | undefined, query?: string) => string
  onCustomizeSections?: () => void
  customizeSectionsButtonRef?: RefObject<HTMLButtonElement | null>
  showInboxOptionsTip?: boolean
  inboxOptionsTipDismissed?: boolean
  optOutLocked?: boolean
  showOptionsButton?: boolean
  showDefaultViewOption?: boolean
  showSidebarTrigger?: boolean
  mobileSidebarFooter?: React.ComponentProps<typeof DashboardSidebarCollapsed>['footer']
  mobileSidebarTriggerRef?: React.ComponentProps<typeof DashboardSidebarCollapsed>['triggerRef']
}

type NewButtonProps = {
  newPath: string
  label: string
}

export function SharedHeader({
  dashboardType,
  links,
  selectedLink,
  onSelectLink,
  showFeedbackDialog = false,
  feedbackSubject = 'dashboard-surface-feedback',
  feedbackPlaceholderText,
  filter,
  rightSideContent,
  primaryAction,
  previewMenu,
  savedViews,
  showSavedViews = false,
  onCreateView,
  onEditView,
  onDeleteView,
  onUpdateView,
  isViewModified = false,
  deleteErrorMessage,
  newButton,
  defaultViewId,
  generateLinkPath,
  onCustomizeSections,
  customizeSectionsButtonRef,
  showInboxOptionsTip = false,
  inboxOptionsTipDismissed = false,
  optOutLocked = false,
  showOptionsButton = true,
  showDefaultViewOption = true,
  showSidebarTrigger = true,
  mobileSidebarFooter,
  mobileSidebarTriggerRef,
}: SharedHeaderProps) {
  const {name = 'Search', description} = selectedLink || {}
  const currentViewId = selectedLink ? selectedLink.path || selectedLink.id : undefined
  const feedbackRef = useRef<FeedbackDialogRef>(null)

  return (
    <div>
      {deleteErrorMessage && (
        <Flash variant="danger" className="tmp-mb-3">
          {deleteErrorMessage}
        </Flash>
      )}
      <div
        className={clsx('d-flex gap-2', !description && 'flex-items-center')}
        style={filter ? HEADER_ROW_WITH_FILTER_STYLE : undefined}
      >
        <div className="flex-1 min-width-0">
          {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
          <div className="d-flex flex-items-top gap-2">
            {showSidebarTrigger && (
              <div className="hide-lg hide-xl">
                <DashboardSidebarCollapsed
                  dashboardType={dashboardType}
                  links={links}
                  selectedLink={selectedLink?.id || ''}
                  onSelectLink={onSelectLink}
                  showSavedViews={showSavedViews}
                  savedViews={savedViews}
                  onCreateView={onCreateView}
                  generateLinkPath={generateLinkPath}
                  footer={mobileSidebarFooter}
                  triggerRef={mobileSidebarTriggerRef}
                />
              </div>
            )}
            <h1
              className={clsx('h3 min-width-0', description ? 'mb-1' : 'mb-0', showSidebarTrigger && 'hide-sm hide-md')}
              data-testid="header-title"
            >
              <Truncate title={name} maxWidth="100%">
                {name}
              </Truncate>
            </h1>
          </div>
          {description && (
            <Truncate className={styles.description} title={description} maxWidth="100%">
              {description}
            </Truncate>
          )}
        </div>
        <div className="d-flex flex-items-center gap-2">
          {!optOutLocked && (
            <>
              {previewMenu ?? <PreviewDropdown showFeedbackDialog={showFeedbackDialog} feedbackRef={feedbackRef} />}
              {showFeedbackDialog && (
                <DashboardFeedbackDialog
                  ref={feedbackRef}
                  subject={feedbackSubject}
                  placeholderText={feedbackPlaceholderText}
                  hideButton
                />
              )}
            </>
          )}
          {rightSideContent && <div className="hide-sm hide-md">{rightSideContent}</div>}
          {primaryAction && <div>{primaryAction}</div>}
          {showOptionsButton && (
            <OptionsButton
              dashboardType={dashboardType}
              currentViewId={currentViewId}
              defaultViewId={defaultViewId}
              selectedLink={selectedLink}
              showSavedViewActions={Boolean(showSavedViews && selectedLink?.source === 'user')}
              onEditView={onEditView}
              onDeleteView={onDeleteView}
              onUpdateView={onUpdateView}
              isViewModified={isViewModified}
              onCustomizeSections={onCustomizeSections}
              customizeSectionsButtonRef={customizeSectionsButtonRef}
              showInboxOptionsTip={showInboxOptionsTip}
              inboxOptionsTipDismissed={inboxOptionsTipDismissed}
              showDefaultViewOption={showDefaultViewOption}
            />
          )}
          {newButton && (
            <Button as="a" href={newButton.newPath} variant="primary" size="medium">
              {newButton.label}
            </Button>
          )}
        </div>
      </div>
      {rightSideContent && (
        <div className="hide-lg hide-xl mb-2" data-testid="right-side-content-mobile">
          {rightSideContent}
        </div>
      )}
      {filter}
    </div>
  )
}

function PreviewDropdown({
  showFeedbackDialog,
  feedbackRef,
}: {
  showFeedbackDialog: boolean
  feedbackRef: React.RefObject<FeedbackDialogRef | null>
}) {
  return (
    <ActionMenu>
      <ActionMenu.Button variant="invisible" size="small">
        <Label variant="success" className={styles.PreviewLabel}>
          Preview
        </Label>
      </ActionMenu.Button>
      <ActionMenu.Overlay>
        <ActionList>
          <ActionList.LinkItem href="https://gh.io/pr-inbox-changelog" target="_blank" rel="noopener noreferrer">
            <ActionList.LeadingVisual>
              <RocketIcon />
            </ActionList.LeadingVisual>
            What&apos;s new
            <ActionList.TrailingVisual>
              <ArrowUpRightIcon />
            </ActionList.TrailingVisual>
          </ActionList.LinkItem>
          {showFeedbackDialog && (
            <ActionList.Item onSelect={() => feedbackRef.current?.openDialog()}>
              <ActionList.LeadingVisual>
                <CommentIcon />
              </ActionList.LeadingVisual>
              Give feedback
            </ActionList.Item>
          )}
          <ActionList.Divider />
          <ActionList.Item
            onSelect={async () => {
              await verifiedFetch('/feature_preview/dashboard_surface_react_app/toggle', {
                method: 'POST',
              })
              window.location.assign('/pulls')
            }}
          >
            <ActionList.LeadingVisual>
              <ArrowSwitchIcon />
            </ActionList.LeadingVisual>
            Switch to the classic experience
          </ActionList.Item>
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
